import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { customerProfileService } from "../../../../../services/api/customerProfileService";
import { formatDate } from "../../../../../utils/DateFormat";
import CustomTable from "../../../components/CustomTable";
import Modal from "../../../components/Modal";

const EditHistoryPopup = ({ isOpen, onClose, profileId, token }) => {
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    if (isOpen) {
      fetchHistory();
    }
  }, [isOpen, profileId]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const response = await customerProfileService.get_edit_history(
        profileId,
        token,
      );
      setHistoryData(response.data);
    } catch (error) {
      console.error("Error fetching history:", error);
      toast.error("Không thể tải lịch sử chỉnh sửa");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      key: "stt",
      title: "STT",
      minWidth: "60px",
      align: "text-center",
      render: (value) => <div className="text-center">{value}</div>,
    },
    {
      key: "noiDung",
      title: "Nội dung chỉnh sửa",
      minWidth: "400px",
      align: "text-left",
      render: (value) => (
        <div className="space-y-1 leading-relaxed">
          {Array.isArray(value) ? (
            value.map((message, idx) => (
              <p key={`message-${idx}`} className="break-words">
                {message}
              </p>
            ))
          ) : (
            <p>{value}</p>
          )}
        </div>
      ),
    },
    {
      key: "ngayChinhSua",
      title: "Ngày chỉnh sửa",
      minWidth: "150px",
      align: "text-center",
      render: (value) => (
        <div className="text-center">
          <p className="font-medium">{value.date}</p>
          <p className="text-xs text-gray-500 mt-1">{value.time}</p>
        </div>
      ),
    },
    {
      key: "nguoiThucHien",
      title: "Người thực hiện",
      minWidth: "200px",
      align: "text-left",
      render: (value) => (
        <div className="flex items-center">
          <span className="font-medium">{value.name}</span>
        </div>
      ),
    },
  ];

  const formattedData = historyData.map((history, index) => ({
    _id: history.editedId || `history-${index}`,
    stt: (currentPage - 1) * itemsPerPage + index + 1,
    noiDung: history.messages || [],
    ngayChinhSua: {
      date: formatDate(history.editedAt),
      time: new Date(history.editedAt).toLocaleTimeString("vi-VN", {
        hour: "2-digit",
        minute: "2-digit",
      }),
    },
    nguoiThucHien: {
      avatar: history.avatar,
      name: history.editedBy,
    },
  }));

  const totalPages = Math.ceil(formattedData.length / itemsPerPage);
  const paginatedData = formattedData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Lịch sử chỉnh sửa"
      maxWidth="1200px"
      primaryButtonText="Đóng"
      primaryButtonOnClick={onClose}
      showCancelButton={false}
    >
      <div className="px-6 py-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : historyData?.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <svg
              className="w-16 h-16 mx-auto mb-4 text-gray-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="text-lg font-medium">Chưa có lịch sử chỉnh sửa</p>
          </div>
        ) : (
          <CustomTable
            data={paginatedData}
            columns={columns}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={totalPages}
            setView={false}
            setEdit={false}
            setDelete={false}
            setDuplicate={false}
          />
        )}
      </div>
    </Modal>
  );
};

export default EditHistoryPopup;
